import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, MessageSquarePlus } from "lucide-react";
import { STATUS_LABEL, STATUS_BADGE, LOAN_PRODUCTS } from "@/lib/loanProducts";
import { toast } from "sonner";

const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const productTitle = (key) => LOAN_PRODUCTS.find((p) => p.key === key)?.title || key;

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/customers/${id}`).then(({ data }) => setData(data));
  useEffect(() => { load(); }, [id]);

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/customers/${id}/notes`, { body: note.trim() });
      setNote("");
      toast.success("Note added");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <DashboardLayout role="admin"><div>Loading…</div></DashboardLayout>;
  const { customer, applications, loans, notes } = data;

  return (
    <DashboardLayout role="admin">
      <Link to="/admin/customers" className="mb-4 inline-flex items-center gap-2 text-sm text-ink-soft hover:text-[#5B2C82]" data-testid="customer-back">
        <ArrowLeft className="h-4 w-4" /> All customers
      </Link>

      <div className="rounded-xl border border-[#E5E2DE] bg-white p-6">
        <h1 className="font-heading text-2xl font-semibold text-ink">{customer.full_name}</h1>
        <div className="text-sm text-ink-soft">{customer.email} · {customer.phone || "—"}</div>
        <div className="mt-1 text-xs text-ink-muted">Joined {new Date(customer.created_at).toLocaleDateString()}</div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-xl border border-[#E5E2DE] bg-white">
            <div className="border-b border-[#E5E2DE] px-5 py-3">
              <h2 className="font-heading text-lg font-semibold text-ink">Applications ({applications.length})</h2>
            </div>
            {applications.length === 0 ? <div className="px-5 py-6 text-sm text-ink-soft">No applications.</div> :
              applications.map((a) => (
                <Link key={a.id} to={`/admin/applications/${a.id}`} className="flex items-center justify-between border-b border-[#E5E2DE] px-5 py-3 last:border-b-0 hover:bg-[#F9F6F0]">
                  <div>
                    <div className="text-sm font-semibold text-ink">{productTitle(a.product)}</div>
                    <div className="text-xs text-ink-muted">{fmt(a.amount)} · {a.term_months} mo · {new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className={`rounded-full border px-2 py-0.5 text-xs ${STATUS_BADGE[a.status]}`}>{STATUS_LABEL[a.status]}</span>
                </Link>
              ))
            }
          </div>

          <div className="rounded-xl border border-[#E5E2DE] bg-white">
            <div className="border-b border-[#E5E2DE] px-5 py-3">
              <h2 className="font-heading text-lg font-semibold text-ink">Loans ({loans.length})</h2>
            </div>
            {loans.length === 0 ? <div className="px-5 py-6 text-sm text-ink-soft">No loans.</div> :
              loans.map((l) => (
                <Link key={l.id} to={`/admin/loans/${l.id}`} className="flex items-center justify-between border-b border-[#E5E2DE] px-5 py-3 last:border-b-0 hover:bg-[#F9F6F0]">
                  <div>
                    <div className="text-sm font-semibold text-ink">{productTitle(l.product)}</div>
                    <div className="text-xs text-ink-muted">{fmt(l.principal)} principal · Balance {fmt(l.balance)}</div>
                  </div>
                  <span className={`rounded-full border px-2 py-0.5 text-xs ${l.status === "active" ? "border-emerald-300 bg-emerald-100 text-emerald-900" : "border-violet-300 bg-violet-100 text-violet-900"}`}>{l.status}</span>
                </Link>
              ))
            }
          </div>
        </div>

        <div className="rounded-xl border border-[#E5E2DE] bg-white p-5">
          <h2 className="font-heading text-lg font-semibold text-ink">CRM notes</h2>
          <div className="mt-3 space-y-2">
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="crm-note-input" />
            <Button onClick={addNote} disabled={busy || !note.trim()} className="w-full bg-[#5B2C82] text-white hover:bg-[#46225f]" data-testid="crm-note-add">
              <MessageSquarePlus className="mr-2 h-4 w-4" /> Add note
            </Button>
          </div>
          <div className="mt-5 space-y-3">
            {notes.length === 0 && <div className="text-xs text-ink-muted">No notes yet.</div>}
            {notes.map((n) => (
              <div key={n.id} className="rounded-lg bg-[#F9F6F0] p-3 text-sm" data-testid={`crm-note-${n.id}`}>
                <div className="text-ink">{n.body}</div>
                <div className="mt-1 text-xs text-ink-muted">{n.author_name} · {new Date(n.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
